import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { observer } from 'mobx-react'
import { Spin, Modal } from 'antd'
import classNames from 'classnames'
import { useMutation } from '@apollo/client'
import styles from './MessageList.module.css'

import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import useInfiniteScroll from '~/hooks/useInfiniteScroll'
import { getGraphData } from '~/utils'
import { MyEmpty } from '~/components/ui/MyEmpty'
import { MyIcon } from '~/components/ui/MyIcon'
import { MessageInfo } from './MessageInfo'
import { unreadInfoItem } from './MessageCard'

import { GET_MESSAGE_NOTICE_LIST, SET_SHIELD_STATUS } from '~/graphqls/gqls'
import {
  msgList,
  msgListVariables,
  msgList_msgList_edges_node,
  BaseMsgType,
  setShieldingStatus,
  setShieldingStatusVariables,
} from '~/graphqls/types'

const PAGE_SIZE = 10

type Props = {
  /** 激活的消息分类 */
  activeMsgItem?: unreadInfoItem
  onListChange: (visible: boolean) => void
  className?: string
}

/** 消息列表 */
export const MessageList: React.FC<Props> = observer(
  ({ activeMsgItem, onListChange, className }) => {
    const [loading, setLoading] = useState(false)
    const [hasNextPage, setHasNextPage] = useState(false)
    const [msgList, setMsgList] = useState<any>([])
    const [isShield, setIsShield] = useState(activeMsgItem?.shield)

    const [modal, contextHolder] = Modal.useModal()
    const getMsgList = useImperativeQuery<msgList, msgListVariables>(
      GET_MESSAGE_NOTICE_LIST
    )
    const [apiShieldStatus] = useMutation<
      setShieldingStatus,
      setShieldingStatusVariables
    >(SET_SHIELD_STATUS)

    const confirmShield = useCallback(() => {
      modal.confirm({
        title: '提示',
        maskStyle: {
          backgroundColor: 'rgba(0,0,0,0.8)',
        },
        width: 270,
        className: `hookModal`,
        content: (
          <div className="text-neutral-400 text-sm text-center">
            是否{isShield ? '取消屏蔽' : '屏蔽'}AI创作的系统通知？
          </div>
        ),
        okText: '确定',
        onOk: close => {
          handleShield()
          close()
        },
        onCancel: close => {
          close()
        },
      })
    }, [isShield])
    const handleShield = useCallback(async () => {
      try {
        const resp = await apiShieldStatus({
          variables: {
            type: activeMsgItem?.baseMsgType as BaseMsgType,
            status: !isShield as boolean,
          },
        })
        if (resp?.data?.status) {
          setIsShield(status => {
            return !status
          })
        }
      } catch (error) {}
    }, [apiShieldStatus, isShield])

    const {
      InfiniteScollComp,
      list,
      hasNextPage: nextPage,
    } = useInfiniteScroll<
      msgList_msgList_edges_node,
      msgList,
      msgListVariables
    >({
      queryDoc: GET_MESSAGE_NOTICE_LIST,
      initData: msgList,
      initPageSize: PAGE_SIZE,
      hasNextPage: hasNextPage,
      infiniteExtraOptions: {
        spinSize: 'default',
        spinStyle: {
          marginTop: 5,
          marginBottom: 20,
        },
      },
      extraQueryParams: {
        type: activeMsgItem?.baseMsgType,
      },
      hasNextPageExtractor: res => {
        return res?.data?.msgList?.pageInfo?.hasNextPage || false
      },
      dataExtractor: res => {
        return getGraphData<msgList_msgList_edges_node>(
          res?.data?.msgList?.edges || []
        )
      },
    })

    const getData = () => {
      setLoading(true)
      getMsgList({
        first: PAGE_SIZE,
        offset: 0,
        type: activeMsgItem?.baseMsgType,
      })
        .then(res => {
          setMsgList(getGraphData(res?.data?.msgList?.edges || []))
          setHasNextPage(res?.data?.msgList?.pageInfo?.hasNextPage || false)
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false)
        })
    }

    useEffect(() => {
      getData()
    }, [])

    return (
      <section
        className={classNames(styles.wrapper, className, 'scrollbar-hidden')}
      >
        <div className="flex justify-between pt-[31px] pb-4 px-4 sticky top-0 bg-neutral-900">
          <MyIcon
            onClick={() => {
              onListChange(false)
            }}
            icon="fanhui"
            className=" text-neutral-400 text-lg cursor-pointer hover:text-primary"
          />
          <span className=" text-base text-neutral-200 font-semibold">
            {activeMsgItem?.showName || ''}
          </span>

          {activeMsgItem?.baseMsgType === BaseMsgType.AI_CREATION ? (
            <MyIcon
              icon={isShield ? 'guanbitongzhi' : 'tongzhi'}
              onClick={confirmShield}
              className="text-neutral-400 text-lg  cursor-pointer hover:text-primary"
            />
          ) : (
            <span className="w-6 h-6"></span>
          )}
        </div>
        {!!list?.length ? (
          <div style={{ paddingBottom: nextPage ? 30 : 16 }} className="px-4">
            {list?.map((msg: msgList_msgList_edges_node) => (
              <MessageInfo msg={msg} key={msg?.id} className={styles.info} />
            ))}
            {InfiniteScollComp}
          </div>
        ) : loading ? (
          <div className={styles.spin}>
            <Spin size="large" />
          </div>
        ) : (
          <MyEmpty />
        )}
        {contextHolder}
      </section>
    )
  }
)
